import React, { useEffect } from 'react';
import { Formik, FormikConfig, FormikValues, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';

import { refocusToFirstError } from 'app/shared/utils/form';
import {
  GenericFormActionProps,
  RenderFormComponent,
} from 'app/shared/typings';

export interface GenericFormProps<T> extends GenericFormActionProps {
  formInitialValues: T;
  renderFormComponent: RenderFormComponent<T>;
  onSubmit: FormikConfig<T>['onSubmit'];
  formSchema?: ObjectSchema<any>;
  validateOnChange?: boolean;
  dataQaId?: string;
}

interface FormComponentContainerProps<T> extends GenericFormActionProps {
  renderFormComponent: RenderFormComponent<T>;
}

const FormComponentContainer = <T extends FormikValues>({
  renderFormComponent,
  setFormSubmitAction,
  setDisplayConfirmation,
  setIsSubmitting,
}: FormComponentContainerProps<T>) => {
  const formikProps = useFormikContext<T>();

  useEffect(() => {
    refocusToFirstError(formikProps.errors, formikProps.isSubmitting);
  });

  // This useEffect and the functions called within it are only for forms embedded in a RoutableModal-style modal
  // (used all over admin) - but GenericForm can be used anywhere, including outside of modals of any kind
  useEffect(() => {
    setFormSubmitAction && setFormSubmitAction(() => formikProps.submitForm);
    setDisplayConfirmation && setDisplayConfirmation(formikProps.dirty);
    setIsSubmitting && setIsSubmitting(formikProps.isSubmitting);
  }, [
    formikProps.isSubmitting,
    formikProps.submitForm,
    formikProps.dirty,
    setFormSubmitAction,
    setDisplayConfirmation,
    setIsSubmitting,
  ]);

  return renderFormComponent({
    formikProps,
  });
};

const GenericForm = <T extends FormikValues>({
  formInitialValues,
  renderFormComponent,
  onSubmit,
  formSchema,
  setFormSubmitAction,
  setDisplayConfirmation,
  setIsSubmitting,
  validateOnChange = true,
  dataQaId,
}: GenericFormProps<T>) => {
  return (
    <>
      <div data-qaid={dataQaId}>
        <Formik<T>
          data-qaid="modal-form-formik"
          initialValues={{
            ...formInitialValues,
          }}
          validationSchema={formSchema}
          onSubmit={onSubmit}
          validateOnBlur={false}
          validateOnChange={validateOnChange}
        >
          <FormComponentContainer
            renderFormComponent={renderFormComponent}
            setFormSubmitAction={setFormSubmitAction}
            setDisplayConfirmation={setDisplayConfirmation}
            setIsSubmitting={setIsSubmitting}
          />
        </Formik>
      </div>
    </>
  );
};

export default GenericForm;
